import React, { useEffect, useRef, useState } from 'react'
import type { Media } from '../../models/attachmentTypes'
import { ChevronLeftIcon, ChevronRightIcon } from '../Icons/ChevronIcons'

interface CarouselProps {
  className?: string
  medias: Array<Media>
  controls?: boolean
  timer?: number
  color?: string
  maxHeight?: string | number
}

/**
 * Media types enum
 */
enum MediaTypes {
  image = 1,
  video
}

const images = ['png', 'gif', 'jpg', 'jpeg']
const videos = ['mp4', 'webm', '3gp', 'ogg']

const Carousel = ({ className, medias, controls, timer, color, maxHeight }: CarouselProps): JSX.Element => {
  const [currentIndex, setCurrentIndex] = useState<number>(0)
  const [currentMedia, setCurrentMedia] = useState<Media>()
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const startTimer = (): void => {
    if (medias.length > 1 && timer !== 0) {
      timerRef.current && clearTimeout(timerRef.current)
      timerRef.current = setTimeout(handleMore, timer || 3000)
    }
  }

  useEffect(() => {
    // Clear timeout
    timerRef.current && clearTimeout(timerRef.current)
    // Call the reset function to set currentIndex and currentMedia states
    startNewMedias()
    // Launch timer only if there are more than 1 media
    startTimer()

    return (): void => {
      timerRef?.current && clearTimeout(timerRef.current)
    }
  }, [medias])

  useEffect(() => {
    if (currentIndex !== undefined) {
      const index = currentIndex % medias.length
      const type = checkType(medias[index].url)
      if (type) {
        setCurrentMedia({
          ...medias[index],
          type: type
        })

        // Reset timer only if there are more than 1 media
        if (medias.length > 1 && timer !== 0) {
          startTimer()
        }
      }
    }
  }, [currentIndex])

  const startNewMedias = (): void => {
    if (currentIndex === 0) {
      const type = checkType(medias[0].url)
      type && setCurrentMedia({ ...medias[0], type: type })
    } else {
      setCurrentIndex(0)
    }
  }

  /**
   *  Retrieve the extension of the media and return if it's either an image, a video, or null
   * @param src : string
   * @returns number | null
   */
  const checkType = (src: string): number | null => {
    const url = src.split('?')[0]
    const extension = url.split('.').slice(-1)[0].toLowerCase()
    return images.includes(extension.toLowerCase()) ? MediaTypes.image : videos.includes(extension.toLowerCase()) ? MediaTypes.video : null
  }

  const handleChoice = (choice: number): void => {
    setCurrentIndex(choice)
  }

  const handleLess = (): void => {
    if (medias.length > 1) {
      currentIndex === 0 ? setCurrentIndex(medias.length - 1) : setCurrentIndex((currentIndex) => currentIndex - 1)
    }
  }

  const handleMore = (): void => {
    if (medias.length > 1) {
      setCurrentIndex((prev) => (prev === medias.length - 1 ? 0 : prev + 1))
    }
  }

  return currentMedia?.type ? (
    <React.Fragment>
      {currentMedia.type === MediaTypes.image ? (
        <img
          className={className}
          src={currentMedia.url}
          alt={currentMedia.alt || 'image'}
          style={{
            maxHeight: maxHeight
          }}
        />
      ) : (
        <video
          className={className}
          style={{
            maxHeight: maxHeight
          }}
          autoPlay
          muted
          loop={medias.length === 1}
          onEnded={handleMore}
        >
          <source src={currentMedia.url} />
        </video>
      )}
      {controls && medias.length > 1 && (
        <React.Fragment>
          {/* Bubbles at the bottom */}
          <div className='rf-absolute rf-bottom-4 rf-left-1/2 -rf-translate-x-1/2 rf-flex rf-flex-row rf-items-center rf-gap-2'>
            {medias.map((media, key) => {
              return (
                <div
                  key={`${media.url}${key}`}
                  className='rf-h-3 rf-w-3 rf-rounded-half rf-border rf-border-truewhite rf-cursor-pointer'
                  onClick={(): void => handleChoice(key)}
                  style={{
                    backgroundColor: currentIndex === key ? color : 'transparent'
                  }}
                />
              )
            })}
          </div>

          {/* Left and right controls */}
          <button aria-label='Previous' className='rf-absolute rf-left-4 rf-top-1/2 -rf-translate-y-1/2 rf-cursor-pointer' onClick={handleLess}>
            <ChevronLeftIcon className='rf-h-6 large:rf-h-8' color='#FFF' />
          </button>
          <button aria-label='Next' className='rf-absolute rf-right-4 rf-top-1/2 -rf-translate-y-1/2 rf-cursor-pointer' onClick={handleMore}>
            <ChevronRightIcon className='rf-h-6 large:rf-h-8' color='#FFF' />
          </button>
        </React.Fragment>
      )}
    </React.Fragment>
  ) : (
    <React.Fragment />
  )
}

export default Carousel
